import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectDropdownWidget } from 'src/components/widget/selectDropdownWidget/SelectDropdownWidget';
import { DyHeader } from 'src/constants';
import {
  type IComponent,
  type IGcfComponentConfig,
  type IGcfProductListGroups,
  DyProductSource,
} from 'src/interfaces';
import { lang } from 'src/utils';

import { shouldDisplayProductsSourceDropdown } from './ProductsSourceController.helper';
import { useGetTwoLineups } from './ProductsSourceController.hook';
import { StyledProductsSourceControllerBox } from './ProductsSourceController.styled';

interface ProductsSourceControllerProps {
  componentDetails: IComponent;
  handleChange: (
    key: string | string[],
    value: any,
    fieldOnChange?: any,
  ) => void;
  handleCacheGcfGroupsChange?: (value: IGcfProductListGroups) => void;
  disabled: boolean;
}

export const ProductsSourceController = ({
  componentDetails,
  handleChange,
  disabled = false,
}: ProductsSourceControllerProps) => {
  const groupNumber = 1;
  const { config }: { config: IGcfComponentConfig } = componentDetails;
  const { gcfGroup } = useGetTwoLineups(
    groupNumber,
    componentDetails.product_category_id,
  );

  const dyHeaders = useMemo(() => config?.dyHeaders || {}, [config?.dyHeaders]);
  const currentSource = config.productsSource;

  const [dyHeadersCache, setDyHeadersCache] = useState<
    Record<DyProductSource, { known: string; unknown: string }>
  >({
    [DyProductSource.DYR]: {
      known: dyHeaders.DYR?.known || DyHeader.recommendation.knownCustomer,
      unknown:
        dyHeaders.DYR?.unknown || DyHeader.recommendation.unknownCustomer,
    },
    [DyProductSource.DYS]: {
      known: dyHeaders.DYS?.known || DyHeader.suggestion.knownCustomer,
      unknown: dyHeaders.DYS?.unknown || DyHeader.suggestion.unknownCustomer,
    },
  });

  useEffect(() => {
    setDyHeadersCache({
      [DyProductSource.DYR]: {
        known: dyHeaders.DYR?.known || DyHeader.recommendation.knownCustomer,
        unknown:
          dyHeaders.DYR?.unknown || DyHeader.recommendation.unknownCustomer,
      },
      [DyProductSource.DYS]: {
        known: dyHeaders.DYS?.known || DyHeader.suggestion.knownCustomer,
        unknown: dyHeaders.DYS?.unknown || DyHeader.suggestion.unknownCustomer,
      },
    });
  }, [dyHeaders]);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (
      initializedRef.current ||
      !currentSource ||
      currentSource === 'Products'
    )
      return;

    const existingKnown = config?.knownCustomer?.header?.text?.trim();
    const existingUnknown = config?.unknownCustomer?.header?.text?.trim();

    if (!existingKnown || !existingUnknown) {
      let known = '';
      let unknown = '';

      if (
        currentSource === DyProductSource.DYR ||
        currentSource === DyProductSource.DYS
      ) {
        known =
          dyHeaders[currentSource]?.known ??
          (currentSource === DyProductSource.DYR
            ? DyHeader.recommendation.knownCustomer
            : DyHeader.suggestion.knownCustomer);

        unknown =
          dyHeaders[currentSource]?.unknown ??
          (currentSource === DyProductSource.DYR
            ? DyHeader.recommendation.unknownCustomer
            : DyHeader.suggestion.unknownCustomer);
      }

      handleChange(
        ['knownCustomer.header.text', 'unknownCustomer.header.text'],
        [known, unknown],
      );
    }

    initializedRef.current = true;
  }, [
    currentSource,
    config?.knownCustomer?.header?.text,
    config?.unknownCustomer?.header?.text,
    dyHeaders,
    handleChange,
  ]);

  const updateHeadersCache = (
    source: DyProductSource,
    known: string,
    unknown: string,
  ) => {
    setDyHeadersCache((prev) => ({
      ...prev,
      [source]: { known, unknown },
    }));
  };

  return shouldDisplayProductsSourceDropdown(
    componentDetails.layoutType?.name,
  ) ? (
    <StyledProductsSourceControllerBox
      data-testid="products-source-controller"
      disabled={disabled}>
      <SelectDropdownWidget
        label={lang.contentSelection}
        value={config?.productsSource || 'Products'}
        selectOptions={[
          { label: lang.singleShelf, value: 'Products' },
          { label: lang.recommended, value: DyProductSource.DYR },
          { label: lang.suggestions, value: DyProductSource.DYS },
        ]}
        onChange={(value: unknown) => {
          const selectedSource = value as DyProductSource | 'Products';

          if (
            currentSource === DyProductSource.DYR ||
            currentSource === DyProductSource.DYS
          ) {
            const currentKnown = config.knownCustomer?.header?.text || '';
            const currentUnknown = config.unknownCustomer?.header?.text || '';

            updateHeadersCache(currentSource, currentKnown, currentUnknown);

            handleChange(
              [
                `dyHeaders.${currentSource}.known`,
                `dyHeaders.${currentSource}.unknown`,
              ],
              [currentKnown, currentUnknown],
            );
          }

          let known = '';
          let unknown = '';

          if (
            selectedSource === DyProductSource.DYR ||
            selectedSource === DyProductSource.DYS
          ) {
            known =
              dyHeadersCache[selectedSource]?.known ??
              dyHeaders[selectedSource]?.known ??
              (selectedSource === DyProductSource.DYR
                ? DyHeader.recommendation.knownCustomer
                : DyHeader.suggestion.knownCustomer);

            unknown =
              dyHeadersCache[selectedSource]?.unknown ??
              dyHeaders[selectedSource]?.unknown ??
              (selectedSource === DyProductSource.DYR
                ? DyHeader.recommendation.unknownCustomer
                : DyHeader.suggestion.unknownCustomer);
          }

          if (selectedSource === 'Products') {
            handleChange(
              [
                'productsSource',
                'header.text',
                'knownCustomer.header.text',
                'unknownCustomer.header.text',
                'productList.groups',
              ],
              [
                selectedSource,
                '',
                DyHeader.recommendation.knownCustomer,
                DyHeader.recommendation.unknownCustomer,
                {},
              ],
            );
          } else {
            const keys = [
              'productsSource',
              'productList.groups',
              `dyHeaders.${selectedSource}.known`,
              `dyHeaders.${selectedSource}.unknown`,
              'knownCustomer.header.text',
              'unknownCustomer.header.text',
            ];

            const values = [
              selectedSource,
              selectedSource === DyProductSource.DYS
                ? { [groupNumber]: gcfGroup }
                : {},
              known,
              unknown,
              known,
              unknown,
            ];

            handleChange(keys, values);
          }
        }}
      />
    </StyledProductsSourceControllerBox>
  ) : null;
};
